import { DOCUMENT, isPlatformServer, NgIf, NgFor, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, PLATFORM_ID } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { Router, RouterLink } from '@angular/router';
import { Cart, CartActions, CartSelectors } from '@next/core-lib/cart';
import { AgrLocationSummaryModule, AgrNotificationEmptyModule } from '@next/core-lib/components';
import { EnvironmentInjector } from '@next/core-lib/environment';
import { AgrI18nSharedModule } from '@next/core-lib/i18n';
import { AgrNotificationModule } from '@next/core-lib/notification';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SelectedCustomer, SelectedLocation, ShipToIdentification, UserSelectors } from '@next/core-lib/user';
import { select, Store } from '@ngrx/store';
import { startOfDay, sub } from 'date-fns';
import * as duration from 'duration-fns';
import { combineLatest, filter, Observable, Subject } from 'rxjs';
import { map, shareReplay, skipWhile, startWith, take, takeUntil } from 'rxjs/operators';
import { Environment } from 'src/environments/environment.model';

import { ReturnButtonComponent } from '../../../product/components/return-button/return-button.component';
import { CustomerBlockedNotificationComponent } from '../../../standalone/customer-blocked-notification/customer-blocked-notification';
import { DeliveryDateWeekendWarningComponent } from '../../../standalone/delivery-date-weekend-warning/delivery-date-weekend-warning.component';
import { SearchComponent } from '../../../standalone/search/search.component';

import { AppState } from '../../models/app.models';

import { ReplaceLinkInTranslationPipe } from '../../shared/pipes/replace-link-in-translation.pipe';
import { CartComponent } from '../cart/cart.component';

interface ViewModel {
  location: SelectedLocation;
  customer: SelectedCustomer;
  carts: Cart[];
  aboutToExpire: boolean;
  isExpired: boolean;
}

@Component({
  selector: 'app-cart-overview',
  templateUrl: './cart-overview.component.html',
  styleUrls: ['./cart-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    AgrLocationSummaryModule,
    ReturnButtonComponent,
    MatDivider,
    CustomerBlockedNotificationComponent,
    SearchComponent,
    AgrNotificationModule,
    DeliveryDateWeekendWarningComponent,
    MatIcon,
    NgFor,
    CartComponent,
    AgrNotificationEmptyModule,
    MatButton,
    RouterLink,
    AsyncPipe,
    AgrI18nSharedModule,
    ReplaceLinkInTranslationPipe,
  ],
})
export class CartOverviewComponent implements OnInit, OnDestroy {
  public readonly destroy$: Subject<void> = new Subject<void>();

  readonly identifications$ = this.store.select(UserSelectors.selectCustomer).pipe(
    filter((customer) => customer !== undefined),
    map(({ shipTos }) =>
      shipTos
        .map((shipTo) =>
          shipTo.identifications.map((identification) => ({
            identificationType: identification.name,
            validFrom: identification.validFrom,
            validTo: identification.validTo,
          })),
        )
        .flat(),
    ),
    shareReplay(1),
  );

  readonly linkToIdentification = `${this.env.MY_OFFICE_URL}/identification`;

  readonly vm$: Observable<ViewModel>;
  private readonly location$: Observable<SelectedLocation>;
  private readonly carts$: Observable<Cart[]>;
  private readonly customer$: Observable<SelectedCustomer>;
  private readonly queryParamCartId$: Observable<string>;

  private aboutToExpire$: Observable<boolean>;
  private isExpired$: Observable<boolean>;

  constructor(
    private readonly router: Router,
    private readonly store: Store<AppState>,
    @Inject(EnvironmentInjector) public env: Environment,
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private readonly platformId: string,
    @Inject(EnvironmentInjector) public environment: Environment,
  ) {
    this.location$ = this.store.select(UserSelectors.selectLocation);
    this.carts$ = this.store.select(CartSelectors.selectCurrentOrderCarts);
    this.customer$ = this.store.select(UserSelectors.selectCustomer);
    this.queryParamCartId$ = this.store.pipe(select(RouterSelectors.getQueryParam('cartId')), take(1));

    this.initExpiredIdentifications();
    this.vm$ = this.setViewModel();
  }

  ngOnInit() {
    this.store.dispatch(CartActions.getCartsRequest({}));

    if (isPlatformServer(this.platformId)) {
      return;
    }

    combineLatest([this.queryParamCartId$, this.carts$])
      .pipe(
        skipWhile(([cartId, carts]) => !cartId || !carts.length),
        take(1),
      )
      .subscribe(([cartId, _]) => {
        this.router.navigate([], { queryParams: { cartId: null }, queryParamsHandling: 'merge' });

        setTimeout(() => {
          this.document.getElementById('cart-' + cartId)?.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
            inline: 'nearest',
          });
        }, 0);
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  trackById(_: number, cart: Cart): number {
    return cart.id;
  }

  private initExpiredIdentifications() {
    // Filter out identifications that do not meet the following conditions:
    // - Identification type is not in the list of visible identifications
    // - Expired identification is no more than configures duration old (typically two months)
    const filteredIdentifications = this.identifications$.pipe(
      map((all) => {
        const now = new Date();
        const identifications = all.filter((identification) => this.env.IDENTIFICATION_TYPES_NL.includes(identification.identificationType));

        return {
          identifications,
          now,
        };
      }),
      takeUntil(this.destroy$),
    );

    this.setAboutToExpire(filteredIdentifications);
    this.setIsExpired(filteredIdentifications);
  }

  private setAboutToExpire(
    filteredIdentifications: Observable<{
      identifications: {
        identificationType: ShipToIdentification;
        validFrom: string;
        validTo: string;
      }[];
      now: Date;
    }>,
  ) {
    this.aboutToExpire$ = filteredIdentifications.pipe(
      map(({ identifications, now }) =>
        identifications.some((identification) => {
          const warningDate = startOfDay(sub(new Date(identification.validTo), duration.parse(this.env.IDENTIFICATION_WARNING_PERIOD)));
          return now >= warningDate && now < startOfDay(new Date(identification.validTo));
        }),
      ),
    );
  }

  private setIsExpired(
    filteredIdentifications: Observable<{
      identifications: {
        identificationType: ShipToIdentification;
        validFrom: string;
        validTo: string;
      }[];
      now: Date;
    }>,
  ) {
    this.isExpired$ = filteredIdentifications.pipe(
      map(({ identifications, now }) => {
        const isExpired: (identification: { identificationType: ShipToIdentification; validFrom: string; validTo: string }) => boolean = (identification) =>
          now > startOfDay(new Date(identification.validTo));

        const allExpiredIdentifications = identifications.filter(isExpired);

        const hasExpiredIdentificationsWithoutValidReplacement = allExpiredIdentifications.some(
          (expiredIdentification) =>
            !identifications.some(
              (identification) => identification.identificationType === expiredIdentification.identificationType && !isExpired(identification),
            ),
        );

        return hasExpiredIdentificationsWithoutValidReplacement;
      }),
    );
  }

  private setViewModel() {
    return combineLatest([
      this.location$.pipe(startWith<SelectedLocation>(null)),
      this.customer$.pipe(startWith<SelectedCustomer>(null)),
      this.carts$.pipe(startWith([])),
      this.aboutToExpire$.pipe(startWith(false)),
      this.isExpired$.pipe(startWith(false)),
    ]).pipe(
      map(([location, customer, carts, aboutToExpire, isExpired]) => ({
        location,
        customer,
        carts,
        aboutToExpire: !isExpired && aboutToExpire,
        isExpired,
      })),
      takeUntil(this.destroy$),
    );
  }
}
